import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { Image } from 'lucide-react';

const UploadButton = styled.button`
  background: none;
  border: none;
  color: #8696a0;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background-color: #f0f2f5;
    color: #111b21;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const ProgressOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const ProgressContainer = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  min-width: 300px;
  text-align: center;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background-color: #e9edef;
  border-radius: 2px;
  overflow: hidden;
  margin: 16px 0;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: #00a884;
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
`;

const PreviewContainer = styled.div`
  margin-bottom: 16px;

  img {
    max-width: 200px;
    max-height: 150px;
    border-radius: 8px;
    object-fit: cover;
  }
`;

const CaptionInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e9edef;
  border-radius: 6px;
  margin: 12px 0;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #00a884;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 16px;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;

  ${props => props.primary ? `
    background-color: #00a884;
    color: white;

    &:hover {
      background-color: #017561;
    }
  ` : `
    background-color: #f0f2f5;
    color: #667781;

    &:hover {
      background-color: #e9edef;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #e53e3e;
  background-color: #fed7d7;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #feb2b2;
  margin: 12px 0;
  font-size: 14px;
  text-align: center;
`;


const ImageUpload = ({ onImageSend, selectedContact, brandCode, disabled = false }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState('');

  const fileInputRef = useRef(null);

const API_URL2 = import.meta.env.VITE_API_URL2;


  const handleImageSelect = (event) => {
    if (disabled) return;

    const file = event.target.files[0];
    if (!file) return;

    // Clear any previous errors
    setError('');

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPEG or PNG)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image too large. Maximum size is 5MB');
      return;
    }

    setSelectedImage(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      setShowPreview(true);
    };
    reader.readAsDataURL(file);

    event.target.value = '';
  };

  const handleSendImage = async () => {
    // Safety checks
    if (!selectedImage) {
      setError('No image selected');
      return;
    }

    if (!selectedContact) {
      setError('No contact selected');
      return;
    }

    if (!selectedContact.phoneNumber) {
      setError('Contact phone number is missing');
      return;
    }

    if (!brandCode) {
      setError('Brand code is missing');
      return;
    }

    if (disabled) {
      setError('Upload is disabled');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setShowPreview(false);
    setError('');

    try {
      const formData = new FormData();
      formData.append('image', selectedImage);
      formData.append('phoneNumber', selectedContact.phoneNumber);
      formData.append('brandCode', brandCode);

      if (caption.trim()) {
        formData.append('caption', caption.trim());
      }

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            // console.log('[ImageUpload] Server response:', response); // Debug log

            if (response.success) {
              // ✅ FIX: Handle different response structures
              let imageData = null;

              if (response.data) {
                // Handle structured response
                imageData = {
                  phoneNumber: selectedContact.phoneNumber,
                  imageUrl: response.data.imageUrl || response.data.url,
                  imageId: response.data.imageId || response.data.id,
                  caption: caption.trim() || response.data.caption,
                  messageId: response.data.messageId || response.data.serverId,
                  timestamp: response.data.timestamp || new Date().toISOString(),
                  brandCode: brandCode
                };
              } else {
                // Handle simple response
                imageData = {
                  phoneNumber: selectedContact.phoneNumber,
                  imageUrl: response.imageUrl || response.url,
                  imageId: response.imageId || response.id || `img-${Date.now()}`,
                  caption: caption.trim(),
                  messageId: response.messageId || response.serverId || `msg-${Date.now()}`,
                  timestamp: response.timestamp || new Date().toISOString(),
                  brandCode: brandCode
                };
              }

              // ✅ FIX: Validate imageData before calling onImageSend
              if (imageData && onImageSend) {
                // console.log('[ImageUpload] Calling onImageSend with:', imageData);
                onImageSend(imageData);
              }

              // Reset form
              setSelectedImage(null);
              setImagePreview(null);
              setCaption('');
              
              // console.log('[ImageUpload] Image sent successfully');
            } else {
              setError(response.message || 'Failed to send image');
            }
          } catch (parseError) {
            console.error('[ImageUpload] Parse error:', parseError);
            setError('Invalid server response');
          }
        } else {
          setError(`Server error: ${xhr.status}`);
        }
        setUploading(false);
      });

      xhr.addEventListener('error', () => {
        console.error('[ImageUpload] Network error');
        setError('Network error occurred');
        setUploading(false);
      });

      xhr.addEventListener('timeout', () => {
        console.error('[ImageUpload] Upload timeout');
        setError('Upload timed out');
        setUploading(false);
      });

      const token = localStorage.getItem('brandToken') || localStorage.getItem('token');

      if (!token) {
        setError('Authentication token missing');
        setUploading(false);
        return;
      }

      // console.log('[ImageUpload] Starting upload for:', selectedContact.phoneNumber);

      xhr.open('POST', API_URL2 + '/api/images/upload');
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('X-Brand-Code', brandCode);
      xhr.timeout = 30000; // 30 second timeout
      xhr.send(formData);

    } catch (error) {
      console.error('[ImageUpload] Upload error:', error);
      setError('Failed to send image. Please try again.');
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setCaption('');
    setShowPreview(false);
    setError('');
  };

  const handleButtonClick = () => {
    if (disabled) return;

    if (!selectedContact) {
      setError('Please select a contact first');
      return;
    }

    if (!selectedContact.phoneNumber) {
      setError('Selected contact has no phone number');
      return;
    }

    fileInputRef.current?.click();
  };

  return (
    <>
      <UploadButton
        onClick={handleButtonClick}
        disabled={uploading || disabled}
        title={disabled ? "Chat is inactive" : "Send Image"}
      >
        <Image size={20} />
      </UploadButton>

      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
        onChange={handleImageSelect}
        disabled={disabled}
      />

      {(showPreview || uploading) && (
        <ProgressOverlay>
          <ProgressContainer>
            {error && <ErrorMessage>{error}</ErrorMessage>}

            {showPreview && !uploading && !error && (
              <>
                <div style={{ marginBottom: '12px', fontWeight: '500' }}>
                  Send Image to {selectedContact?.name || selectedContact?.phoneNumber}
                </div>

                <PreviewContainer>
                  <img src={imagePreview} alt="Preview" />
                </PreviewContainer>

                <CaptionInput
                  type="text"
                  placeholder="Add a caption..."
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  maxLength={1024}
                />

                <ButtonGroup>
                  <ActionButton onClick={handleCancel}>
                    Cancel
                  </ActionButton>
                  <ActionButton primary onClick={handleSendImage}>
                    Send Image
                  </ActionButton>
                </ButtonGroup>
              </>
            )}

            {uploading && (
              <>
                <div style={{ marginBottom: '12px', fontWeight: '500' }}>
                  Sending Image...
                </div>

                <ProgressBar>
                  <ProgressFill progress={uploadProgress} />
                </ProgressBar>

                <div style={{ fontSize: '14px', color: '#667781' }}>
                  {uploadProgress}% uploaded
                </div>

                {uploadProgress < 100 && (
                  <ActionButton
                    onClick={() => {
                      setUploading(false);
                      setShowPreview(true);
                    }}
                    style={{ marginTop: '16px' }}
                  >
                    Cancel Upload
                  </ActionButton>
                )}
              </>
            )}

            {error && !showPreview && !uploading && (
              <ButtonGroup>
                <ActionButton onClick={handleCancel}>
                  Close
                </ActionButton>
              </ButtonGroup>
            )}
          </ProgressContainer>
        </ProgressOverlay>
      )}
    </>
  );
};

export default ImageUpload;
